import { getShopIdFromLocation } from 'util/appUtils';

const READ_ACTIONS = new Set([
  'catalog_selectors.discover',
  'catalog_selectors.get_active',
  'availability_rules.discover',
  'availability_criteria.get_active',
]);

function generatedMutationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `catalog-selection-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const actionPath = (shopId, action) => (
  `/system/shop/${encodeURIComponent(shopId)}/catalog-selection/actions/${READ_ACTIONS.has(action) ? 'read' : 'write'}`
);

function unwrap(response) {
  if (!response || typeof response !== 'object') {
    const error = new Error('The catalog selection service returned an invalid response.');
    error.code = 'INVALID_RESPONSE';
    throw error;
  }
  if (response.result === 'error') {
    const error = new Error(response.error || 'Catalog selection request failed.');
    error.code = response.code || 'CATALOG_SELECTION_REQUEST_FAILED';
    error.details = response.details || [];
    throw error;
  }
  return response;
}

/** Cloud/web-service transport for the shared catalog selector bridge contract. */
export function createLegacyCatalogSelectorApi({
  client,
  shopId,
  mutationIdFactory = generatedMutationId,
} = {}) {
  let resolvedClient = client || null;
  let revision = null;
  const pending = new Map();

  const getClient = () => {
    if (!resolvedClient) {
      // eslint-disable-next-line global-require
      resolvedClient = require('util/appUtils').wsClient;
    }
    return resolvedClient;
  };
  const getShopId = () => shopId || getShopIdFromLocation();

  async function send(action, payload = {}) {
    return unwrap(await getClient().prepareRequest(
      'POST',
      actionPath(getShopId(), action),
      { body: { action, payload } },
    ));
  }

  function rememberRevision(response) {
    if (Number.isInteger(Number(response?.config_revision))) {
      revision = Number(response.config_revision);
    }
    return response;
  }

  async function ensureRevision() {
    if (revision === null) await api.getActive();
    return revision;
  }

  async function mutate(action, payload) {
    const signature = JSON.stringify(payload);
    const previous = pending.get(action);
    const mutation = previous?.signature === signature
      ? previous
      : { signature, id: mutationIdFactory() };
    pending.set(action, mutation);
    const response = rememberRevision(await send(action, {
      ...payload,
      expected_revision: await ensureRevision(),
      client_mutation_id: mutation.id,
    }));
    pending.delete(action);
    return response;
  }

  const api = {
    discoverSelectors() {
      return send('catalog_selectors.discover');
    },

    async getActive() {
      pending.clear();
      return rememberRevision(await send('catalog_selectors.get_active'));
    },

    setSelector(selectorKey, config = {}) {
      return mutate('catalog_selectors.set', { selector_key: selectorKey, config });
    },

    discoverAvailabilityRules() {
      return send('availability_rules.discover');
    },

    async getActiveAvailabilityCriteria() {
      return rememberRevision(await send('availability_criteria.get_active'));
    },

    setAvailabilityCriteria(ruleKey, params = {}, fallbackWhenAbsent = true) {
      return mutate('availability_criteria.set', {
        available_rule_key: ruleKey,
        rule_params: params,
        fallback_when_absent: fallbackWhenAbsent,
      });
    },
  };

  return api;
}

export { READ_ACTIONS as CATALOG_SELECTION_READ_ACTIONS };
